import * as z from 'zod'
import styled from '@emotion/styled'
import { zodResolver } from '@hookform/resolvers/zod'
import React from 'react'
import { useForm } from 'react-hook-form'
import isURL from 'validator/lib/isURL'

import { isEmpty } from '@livechat/data-utils'

import Button from 'components/Button'
import InfoBanner from 'components/InfoBanner'
import Fieldset from 'components/fields/Fieldset'
import Form from 'components/fields/Form'
import Input from 'components/fields/Input'

const StyledInfoBanner = styled(InfoBanner)`
	margin-top: 0;
`

const StyledNewURLForm = styled(Form)`
	max-width: 380px;
	padding: 0;
`

const Row = styled.div`
	display: grid;
	grid-auto-flow: column;
	gap: ${({ theme }) => theme.spaces.space4};
	position: relative;
`

const StyledButton = styled(Button)`
	padding: ${({ theme }) =>
		theme.isMobile ? `${theme.spaces.space3} ${theme.spaces.space4}` : `14px ${theme.spaces.space6}`};
	align-self: end;
	margin-bottom: ${({ theme }) => (theme.isMobile ? '12px' : '11px')};
`

const LabelWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.spaces.space3};
`

const FormSchema = z.object({
	newLinkURL: z.union([z.literal(''), z.string().refine(isURL, 'Invalid URL format')]),
})

type FormValues = z.infer<typeof FormSchema>

type Props = {
	onAddNewLink: (link: string) => void
	isLinksLimitReached: boolean
}

const NewLinkForm: React.FC<Props> = ({ onAddNewLink, isLinksLimitReached }) => {
	const { register, handleSubmit, formState, getValues, reset } = useForm<FormValues>({
		mode: 'onTouched',
		resolver: zodResolver(FormSchema),
	})
	const { errors } = formState

	const onSubmit = (data: FormValues) => {
		if (!isEmpty(errors['newLinkURL'] ?? []) || isLinksLimitReached || !getValues('newLinkURL')) {
			return
		}

		const { newLinkURL } = data
		if (!newLinkURL) {
			return
		}

		onAddNewLink(newLinkURL)
		reset()
	}

	return (
		<StyledNewURLForm onSubmit={handleSubmit(onSubmit)}>
			<Fieldset>
				<Row>
					<Input
						label={<LabelWrapper>Create link</LabelWrapper>}
						placeholder="Paste your URL here"
						error={errors.newLinkURL?.message}
						disabled={isLinksLimitReached}
						{...register('newLinkURL')}
					/>
					<StyledButton kind="primary" type="submit" disableHover>
						Create
					</StyledButton>
				</Row>
				{isLinksLimitReached && (
					<StyledInfoBanner>{`You've reached the maximum number of Custom Links.`}</StyledInfoBanner>
				)}
			</Fieldset>
		</StyledNewURLForm>
	)
}

export default NewLinkForm
